import React, { useState, useEffect } from 'react';
import { AdapterLuxonSunday } from '../../utils/AdapterSundayLuxon';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTime } from 'luxon';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import InputMask from 'react-input-mask';
import axios from '../../api/axios';
import { useSnackbar } from 'notistack';
import { useUser } from '../../Hooks/useUser';
import { useFormData } from "../../Hooks/useFormDataContext";
import { normalizeUsStateAbbrev } from './usStateFormatting';

const endpoint = '/api/users/alluserinfo';

function parseProfileDob(raw) {
  if (raw === null || raw === undefined || raw === '') return null;
  const s = String(raw).trim();
  if (!s) return null;

  let dt = DateTime.fromISO(s);
  if (dt.isValid) return dt.startOf('day');

  dt = DateTime.fromFormat(s, 'yyyy-MM-dd');
  if (dt.isValid) return dt;

  dt = DateTime.fromFormat(s, 'M/d/yyyy');
  if (dt.isValid) return dt;

  dt = DateTime.fromFormat(s, 'MM/dd/yyyy');
  if (dt.isValid) return dt;

  const js = new Date(s);
  if (!Number.isNaN(js.getTime())) return DateTime.fromJSDate(js).startOf('day');

  return null;
}

function normalizeSettings(s) {
  return {
    ...s,
    BP_G_AddressState: normalizeUsStateAbbrev(s?.BP_G_AddressState) || '',
  };
}

const General = (props) => {
  const { user: contextUser } = useUser();
  const { enqueueSnackbar } = useSnackbar();
  const { updateForm } = useFormData();

  // Admin mode passes User/UserSettings explicitly (even while still loading, as null).
  // Profile mode never passes these props at all.
  const isControlled = Object.prototype.hasOwnProperty.call(props, 'User');

  const [User, setUser] = useState(props.User ?? {});
  const [UserSettings, setUserSettings] = useState(normalizeSettings(props.UserSettings ?? {}));
  const [DOB, setDOB] = useState(parseProfileDob(props.User?.BP_DOB));
  const [saving, setSaving] = useState(false);

  // Admin mode: sync local state whenever the parent's fetched data arrives/changes.
  // No self-fetch here — the parent already owns this request.
  useEffect(() => {
    if (!isControlled || !props.User) return;
    setUser(props.User);
    setDOB(parseProfileDob(props.User.BP_DOB));
  }, [isControlled, props.User]);

  useEffect(() => {
    if (!isControlled || !props.UserSettings) return;
    setUserSettings(normalizeSettings(props.UserSettings));
  }, [isControlled, props.UserSettings]);

  // Profile mode only: self-fetch based on the logged-in user, with race protection.
  useEffect(() => {
    if (isControlled) return;
    if (!contextUser?.UserID) return;

    let cancelled = false;

    const fetchData = async () => {
      try {
        const response = await axios.get(`${endpoint}/${contextUser.UserID}`);
        if (cancelled) return;
        const u = response.data.user || {};
        const s = response.data.userSettings || {};
        setUser(u);
        setUserSettings(normalizeSettings(s));
        setDOB(parseProfileDob(u.BP_DOB));
      } catch (e) {
        if (!cancelled) {
          console.error(e);
          enqueueSnackbar('User fetch failed', { variant: 'error' });
        }
      }
    };

    fetchData();
    return () => { cancelled = true; };
  }, [isControlled, contextUser?.UserID, enqueueSnackbar]);

  function handleDateChange(value) {
    setDOB(value);
    const apiDob = value && value.isValid ? value.toISODate() : null;
    setUser(prevUserData => ({ ...prevUserData, BP_DOB: apiDob }));
    if (props.UseFormContext) {
      updateForm('User', 'BP_DOB', apiDob);
    }
  }

  function toIntSelectValue(value) {
  if (value === '' || value === undefined || value === null || value === '(select)') return null;
  const n = parseInt(value, 10);
  return Number.isNaN(n) ? null : n;
}

function handleIntSelectChange(input) {
  const target = input.target;
  const name = target.id;
  const value = toIntSelectValue(target.value);
  setUserSettings(prev => ({ ...prev, [name]: value }));
  if (props.UseFormContext) {
    updateForm("UserSettings", name, value);
  }
}

  function handleUserFieldChange(input) {
    const target = input.target;
    const value = target.value;
    const name = target.id;
    setUser(prev => ({ ...prev, [name]: value }));
    if (props.UseFormContext) {
      updateForm("User", name, value);
    }
  }

  function handleUserSettingsFieldChange(input) {
    const target = input.target;
    const value = target.value;
    const name = target.id;
    setUserSettings(prev => ({ ...prev, [name]: value }));
    if (props.UseFormContext) {
      updateForm("UserSettings", name, value);
    }
  }

  const handleSubmit = async e => {
    e.preventDefault();

    let dobForSave;
    if (DOB && DateTime.isDateTime(DOB) && DOB.isValid) {
      dobForSave = DOB.toISODate();
    } else if (!DOB) {
      const raw = User?.BP_DOB;
      dobForSave = raw === '' || raw === undefined || raw === null ? null : String(raw).trim() || null;
    } else {
      enqueueSnackbar('Invalid date format. Please check your date of birth.', { variant: 'error' });
      return;
    }

    const updatedUser = { ...User, BP_DOB: dobForSave };
    const sanitizedUserSettings = { ...UserSettings };

    if (sanitizedUserSettings.BP_Regions && sanitizedUserSettings.BP_Regions !== "(select)") {
      sanitizedUserSettings.BP_Regions = parseInt(sanitizedUserSettings.BP_Regions, 10);
    } else if (sanitizedUserSettings.BP_Regions === "(select)") {
      sanitizedUserSettings.BP_Regions = null;
    }

    if (sanitizedUserSettings.UserGender && sanitizedUserSettings.UserGender !== "(select)") {
      sanitizedUserSettings.UserGender = parseInt(sanitizedUserSettings.UserGender, 10);
    } else if (sanitizedUserSettings.UserGender === "(select)") {
      sanitizedUserSettings.UserGender = null;
    }

    setSaving(true);
    try {
      await axios.put('/api/users/alluserinfo', { user: updatedUser, userSettings: sanitizedUserSettings });
      enqueueSnackbar("General Section Saved!");
    } catch (ex) {
      console.error(ex);
      enqueueSnackbar("Saving user failed: " + (ex.response?.data?.error || ex.message), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

    return(
      <form onSubmit={handleSubmit}>
        <div id="tab1">
        <div className="row px-5 pt-5">
          <div className="col-md-6">
            <div className="form-group">
              <label className="required-caption">First Name<span className="required-field">*</span></label>
              <input type="text" name="FirstName" id="FirstName" value={User?.FirstName} maxLength="200" className="form-control" placeholder="Enter first name" onChange={handleUserFieldChange}/>
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label className="required-caption">Last Name<span className="required-field">*</span></label>
              <input type="text" name="LastName" id="LastName" value={User?.LastName} maxLength="200" className="form-control" placeholder="Enter last name" onChange={handleUserFieldChange}/>
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group">
            <label className="required-caption">Date of Birth<span className="required-field">*</span></label>
            <LocalizationProvider dateAdapter={AdapterLuxonSunday}>
            
<DatePicker
    slotProps={{
        textField: {
            id: "BP_DOB",
        },
    }}
    value={DOB}
    onChange={handleDateChange}
    format="MM/dd/yyyy"
/>
            </LocalizationProvider>
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label id="" className="required-caption" htmlFor="UserGender">Gender<span className="required-field">*</span></label>
              <select name="UserGender" id="UserGender" value={UserSettings?.UserGender != null ? String(UserSettings.UserGender) : '(select)'} className="DropDownField form-control" onChange={handleIntSelectChange}>
                <option value="(select)">Select</option>
                <option value="1">Male</option>
                <option value="0">Female</option>
              </select>
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label id="" className="required-caption" htmlFor="BP_G_Marital">Marital Status<span className="required-field">*</span></label>
              <select name="BP_G_Marital" id="BP_G_Marital" className="DropDownField form-control" value={UserSettings?.BP_G_Marital} onChange={handleUserSettingsFieldChange}>
                <option value="(select)">Select</option>
                <option value="Single">Single</option>
                <option value="Married">Married</option>
                <option value="Divorced">Divorced</option>
                <option value="Widowed">Widowed</option>
              </select>
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label htmlFor="BP_BGCheckDate">Background Check Date</label>
              <input
                type="text"
                id="BP_BGCheckDate"
                name="BP_BGCheckDate"
                className="form-control"
                readOnly
                value={
                  UserSettings?.BP_BGCheckDate
                    ? new Date(UserSettings.BP_BGCheckDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
                    : 'Not on record'
                }
                style={{ backgroundColor: '#f5f5f5', cursor: 'default' }}
              />
            </div>
          </div>
        </div>
        <hr/>
        <div className="row px-5 pt-5">
          <div className="col-md-12 mb-2">
            <h6>Contact Information</h6>
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label className="required-caption">Phone Number<span className="required-field">*</span></label>
              <InputMask 
                mask="(999) 999-9999" 
                value={UserSettings?.BP_G_Mob_Phone || ''} 
                onChange={handleUserSettingsFieldChange}
              >
                {(inputProps) => (
                  <input 
                    {...inputProps}
                    name="BP_G_Mob_Phone" 
                    id="BP_G_Mob_Phone" 
                    type="text" 
                    className="form-control" 
                    placeholder="(123) 456-7890"
                  />
                )}
              </InputMask>
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label id="" className="required-caption" htmlFor="BP_Regions">County/Region:</label>
              <select name="BP_Regions" id="BP_Regions" className="DropDownField form-control" value={UserSettings?.BP_Regions} onChange={handleIntSelectChange}>
                <option value="(select)">Select</option>
                <option value="1">Alamance- Region 4</option>
                <option value="2">Alexander- Region 7</option>
                <option value="3">Anson- Region 6</option>
                <option value="4">Ashe - Region 7</option>
                <option value="5">Avery - Region 7</option>
                <option value="6">Beaufort - Region 2</option>
                <option value="7">Bertie - Region 1</option>
                <option value="8">Bladen - Region 3</option>
                <option value="9">Brunswick - Region 3</option>
                <option value="10">Buncombe - Region 9</option>
                <option value="11">Burke - Region 8</option>
                <option value="12">Cabarrus - Region 6</option>
                <option value="13">Caldwell - Region 7</option>
                <option value="14">Camden - Region 1</option>
                <option value="15">Carteret - Region 2</option>
                <option value="16">Caswell - Region 4</option>
                <option value="17">Catawba - Region 8</option>
                <option value="18">Chatham - Region 4</option>
                <option value="19">Cherokee - Region 10</option>
                <option value="20">Chowan - Region 1</option>
                <option value="21">Clay - Region 10</option>
                <option value="22">Cleveland - Region 8</option>
                <option value="23">Columbus - Region 3</option>
                <option value="24">Craven - Region 2</option>
                <option value="25">Cumberland - Region 3</option>
                <option value="26">Currituck - Region 1</option>
                <option value="27">Dare - Region 1</option>
                <option value="28">Davidson - Region 5</option>
                <option value="29">Davie - Region 8</option>
                <option value="30">Duplin - Region 3</option>
                <option value="31">Durham - Region 4</option>
                <option value="32">Edgecombe - Region 2</option>
                <option value="33">Forsyth - Region 5</option>
                <option value="34">Franklin - Region 4</option>
                <option value="35">Gaston - Region 8</option>
                <option value="36">Gates - Region 1</option>
                <option value="37">Graham - Region 10</option>
                <option value="38">Granville - Region 4</option>
                <option value="39">Greene - Region 2</option>
                <option value="40">Guilford - Region 5</option>
                <option value="41">Halifax - Region 2</option>
                <option value="42">Harnett - Region 4</option>
                <option value="43">Haywood - Region 9</option>
                <option value="44">Henderson - Region 9</option>
                <option value="45">Hertford - Region 1</option>
                <option value="46">Hoke - Region 3</option>
                <option value="47">Hyde - Region 1</option>
                <option value="48">Iredell - Region 8</option>
                <option value="49">Jackson - Region 10</option>
                <option value="50">Johnston - Region 4</option>
                <option value="51">Jones - Region 2</option>
                <option value="52">Lee - Region 4</option>
                <option value="53">Lenoir - Region 2</option>
                <option value="54">Lincolnton - Region 8</option>
                <option value="55">Macon - Region 10</option>
                <option value="56">Madison - Region 9</option>
                <option value="57">Martin - Region 2</option>
                <option value="58">McDowell - Region 8</option>
                <option value="59">Mecklenburg - Region 6</option>
                <option value="60">Mitchell - Region 9</option>
                <option value="61">Montgomery - Region 6</option>
                <option value="62">Moore - Region 4</option>
                <option value="63">Nash - Region 2</option>
                <option value="64">New Hanover - Region 3</option>
                <option value="65">Northhampton - Region 1</option>
                <option value="66">Onslow - Region 2</option>
                <option value="67">Orange - Region 4</option>
                <option value="68">Pamlico - Region 2</option>
                <option value="69">Pasqoutank - Region 1</option>
                <option value="70">Pender - Region 3</option>
                <option value="71">Perquimans - Region 1</option>
                <option value="72">Person - Region 4</option>
                <option value="73">Pitt - Region 2</option>
                <option value="74">Polk - Region 8</option>
                <option value="75">Randolph - Region 5</option>
                <option value="76">Richmond - Region 6</option>
                <option value="77">Robeson - Region 3</option>
                <option value="78">Rockingham - Region 5</option>
                <option value="79">Rowan - Region 5</option>
                <option value="80">Rutherford - Region 8</option>
                <option value="81">Sampson - Region 3</option>
                <option value="82">Scotland - Region 6</option>
                <option value="83">Stanly - Region 6</option>
                <option value="84">Stokes - Region 5</option>
                <option value="85">Surry - Region 7</option>
                <option value="86">Swain - Region 10</option>
                <option value="87">Transylvania - Region 9</option>
                <option value="88">Tyrrell - Region 1</option>
                <option value="89">Union - Region 6</option>
                <option value="90">Vance - Region 4</option>
                <option value="91">Wake - Region 4</option>
                <option value="92">Warren - Region 4</option>
                <option value="93">Washington - Region 1</option>
                <option value="94">Watauga - Region 7</option>
                <option value="95">Wayne - Region 2</option>
                <option value="96">Wilkes - Region 7</option>
                <option value="97">Wilmington - Region 3</option>
                <option value="98">Wilson - Region 2</option>
                <option value="99">Yadkin - Region 7</option>
                <option value="100">Yancey - Region 9</option>
                <option value="101">Other - If you're from outside NC (Region 11)</option>
              </select>
            </div>
          </div>
        </div>
        <hr/>
        <div className="row px-5 pt-5">
          <div className="col-md-12 mb-2">
            <h6>Address</h6>
          </div>
          <div className="col-md-12">
            <div className="form-group">
              <label className="required-caption">Address<span className="required-field">*</span></label>
              <input type="text" maxLength="200" name="BP_G_Address" id="BP_G_Address" value={UserSettings?.BP_G_Address} onChange={handleUserSettingsFieldChange} className="form-control" placeholder="Address"/>
            </div>
          </div>
          <div className="col-md-12">
            <div className="form-group mt-2">
              <input type="text" name="BP_G_AddressLine2" id="BP_G_AddressLine2"  value={UserSettings?.BP_G_AddressLine2} onChange={handleUserSettingsFieldChange} maxLength="200" className="form-control" placeholder="Apartment, suite, etc. (optional)"/>
            </div>
          </div>
          <div className="col-md-4">
            <div className="form-group">
              <label className="required-caption">City<span className="required-field">*</span></label>
              <input type="text" maxLength="200" name="BP_G_AddressCity" id="BP_G_AddressCity"  value={UserSettings?.BP_G_AddressCity} onChange={handleUserSettingsFieldChange} className="form-control" placeholder="City"/>
            </div>
          </div>
          <div className="col-md-4">
            <div className="form-group">
              <label id="" className="required-caption" htmlFor="BP_G_AddressState">State</label>
              <select name="BP_G_AddressState" id="BP_G_AddressState" value={UserSettings?.BP_G_AddressState} onChange={handleUserSettingsFieldChange} className="DropDownField form-control">
                <option value=""></option>
                <option value="AL">Alabama</option>
                <option value="AK">Alaska</option>
                <option value="AZ">Arizona</option>
                <option value="AR">Arkansas</option>
                <option value="CA">California</option>
                <option value="CO">Colorado</option>
                <option value="CT">Connecticut</option>
                <option value="DE">Delaware</option>
                <option value="DC">District of Columbia</option>
                <option value="FL">Florida</option>
                <option value="GA">Georgia</option>
                <option value="HI">Hawaii</option>
                <option value="ID">Idaho</option>
                <option value="IL">Illinois</option>
                <option value="IN">Indiana</option>
                <option value="IA">Iowa</option>
                <option value="KS">Kansas</option>
                <option value="KY">Kentucky</option>
                <option value="LA">Louisiana</option>
                <option value="ME">Maine</option>
                <option value="MD">Maryland</option>
                <option value="MA">Massachusetts</option>
                <option value="MI">Michigan</option>
                <option value="MN">Minnesota</option>
                <option value="MS">Mississippi</option>
                <option value="MO">Missouri</option>
                <option value="MT">Montana</option>
                <option value="NE">Nebraska</option>
                <option value="NV">Nevada</option>
                <option value="NH">New Hampshire</option>
                <option value="NJ">New Jersey</option>
                <option value="NM">New Mexico</option>
                <option value="NY">New York</option>
                <option value="NC">North Carolina</option>
                <option value="ND">North Dakota</option>
                <option value="OH">Ohio</option>
                <option value="OK">Oklahoma</option>
                <option value="OR">Oregon</option>
                <option value="PA">Pennsylvania</option>
                <option value="RI">Rhode Island</option>
                <option value="SC">South Carolina</option>
                <option value="SD">South Dakota</option>
                <option value="TN">Tennessee</option>
                <option value="TX">Texas</option>
                <option value="UT">Utah</option>
                <option value="VT">Vermont</option>
                <option value="VA">Virginia</option>
                <option value="WA">Washington</option>
                <option value="WV">West Virginia</option>
                <option value="WI">Wisconsin</option>
                <option value="WY">Wyoming</option>
              </select>
            </div>
          </div>
          <div className="col-md-4">
            <div className="form-group">
              <label className="required-caption">ZIP Code<span className="required-field">*</span></label>
              <input type="text" name="BP_G_AddressZip" id="BP_G_AddressZip" value={UserSettings?.BP_G_AddressZip} onChange={handleUserSettingsFieldChange} maxLength="200" className="form-control" placeholder="ZIP Code"/>
            </div>
          </div>
        </div>        
        <hr/>
        {
          !props.UseFormContext ?
        
        <div className="row my-5">
          <div className="col-md-12 text-center"> 
            <a className="button-default" onClick={handleSubmit} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px', minHeight: '38px', minWidth: '120px', padding: '8px 20px', opacity: saving ? 0.7 : 1, pointerEvents: saving ? 'none' : 'auto' }}>
              {saving && <CircularProgress size={14} style={{ color: 'white' }} />}
              Save
            </a> 
          </div>
        </div> : "" }
      </div>
      </form>

    )
}
export default General;